import { useCallback, useEffect, useState } from 'react';
import {
  applyCourseDesign,
  createCourseTemplate,
  deleteCourseTemplate,
  getCourseTemplates,
  getMyCourses,
  previewCourseDesign,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { AlertTriangle, Copy, Layers, Loader2, Save, Trash2 } from 'lucide-react';

const PARTS = [
  { id: 'modules', label: 'Modules' },
  { id: 'lessons', label: 'Lessons (with video and materials)' },
  { id: 'assignments', label: 'Assignments' },
  { id: 'exams', label: 'Exams' },
  { id: 'resources', label: 'Course materials' },
  { id: 'sessions', label: 'Live session skeleton (no dates or links)' },
];

const DEFAULT_INCLUDE = {
  modules: true,
  lessons: true,
  assignments: true,
  exams: true,
  resources: true,
  sessions: false,
};

function PartPicker({ include, onChange, counts }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {PARTS.map((part) => (
        <label key={part.id} className="flex items-center gap-2 rounded-lg border border-[#062B1A]/10 p-2.5 text-sm">
          <Checkbox
            checked={!!include[part.id]}
            onCheckedChange={(checked) => onChange({ ...include, [part.id]: !!checked })}
            aria-label={part.label}
          />
          <span className="flex-1 text-[#062B1A]/75">{part.label}</span>
          {counts?.[part.id] != null && (
            <Badge variant="outline" className="text-[10px]">{counts[part.id]}</Badge>
          )}
        </label>
      ))}
    </div>
  );
}

/**
 * Reuse a course design.
 *
 * Two directions, because instructors want both: copy an existing course's
 * design onto this one, or freeze this course's design as a template to apply
 * to future classes. Either way only authored content moves — the roster,
 * grades, attendance and submissions of both courses are untouched.
 */
export default function CourseDesignDialog({ open, onOpenChange, courseId, courseTitle, onApplied }) {
  const [courses, setCourses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const [sourceCourseId, setSourceCourseId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [include, setInclude] = useState(DEFAULT_INCLUDE);
  const [mode, setMode] = useState('append');
  const [publishState, setPublishState] = useState('draft');
  const [sourceCounts, setSourceCounts] = useState(null);

  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [saveInclude, setSaveInclude] = useState(DEFAULT_INCLUDE);
  const [ownCounts, setOwnCounts] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [courseRes, templateRes, previewRes] = await Promise.all([
        getMyCourses(),
        getCourseTemplates(),
        previewCourseDesign(courseId).catch(() => ({ data: { counts: null } })),
      ]);
      setCourses((courseRes.data || []).filter((course) => course.id !== courseId));
      setTemplates(templateRes.data || []);
      setOwnCounts(previewRes.data?.counts || null);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not load your courses');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Show how much content the chosen source would actually contribute.
  useEffect(() => {
    let cancelled = false;
    if (!sourceCourseId) {
      setSourceCounts(null);
      return undefined;
    }
    previewCourseDesign(sourceCourseId)
      .then(({ data }) => { if (!cancelled) setSourceCounts(data?.counts || null); })
      .catch(() => { if (!cancelled) setSourceCounts(null); });
    return () => { cancelled = true; };
  }, [sourceCourseId]);

  const handleApply = async () => {
    if (!sourceCourseId && !templateId) {
      toast.error('Pick a course or a template to copy from');
      return;
    }
    try {
      setWorking(true);
      const { data } = await applyCourseDesign({
        target_course_id: courseId,
        ...(sourceCourseId ? { source_course_id: sourceCourseId } : { template_id: templateId }),
        include,
        mode,
        publish_state: publishState,
      });
      const created = data?.created || {};
      const summary = Object.entries(created)
        .filter(([, count]) => count > 0)
        .map(([part, count]) => `${count} ${part}`)
        .join(', ');
      toast.success(summary ? `Copied ${summary}` : 'Nothing to copy from that design');
      if (data?.media_copy_failures) {
        toast.warning(`${data.media_copy_failures} file${data.media_copy_failures === 1 ? '' : 's'} could not be copied and were left out.`);
      }
      onOpenChange(false);
      onApplied?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not apply that design');
    } finally {
      setWorking(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setWorking(true);
      await createCourseTemplate({
        source_course_id: courseId,
        name: templateName.trim(),
        description: templateDescription.trim(),
        include: saveInclude,
      });
      toast.success('Design saved as a template');
      setTemplateName('');
      setTemplateDescription('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save this design');
    } finally {
      setWorking(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    try {
      setWorking(true);
      await deleteCourseTemplate(template.id);
      setTemplates((prev) => prev.filter((item) => item.id !== template.id));
      toast.success('Template deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete that template');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Reuse a course design</DialogTitle>
          <DialogDescription>
            Copy the teaching design of another course into <strong>{courseTitle}</strong>, or save this
            course's design so you can apply it to future classes. Students, grades, attendance and
            submissions are never copied.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="size-6 animate-spin text-[#D4AF37]" /></div>
        ) : (
          <Tabs defaultValue="apply" className="space-y-4">
            <TabsList className="bg-[#F1F3F2]">
              <TabsTrigger value="apply" className="data-[state=active]:bg-[#062B1A] data-[state=active]:text-white">
                <Copy className="mr-2 size-4" />Copy into this course
              </TabsTrigger>
              <TabsTrigger value="save" className="data-[state=active]:bg-[#062B1A] data-[state=active]:text-white">
                <Save className="mr-2 size-4" />Save this design
              </TabsTrigger>
            </TabsList>

            <TabsContent value="apply" className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">From another course</label>
                  <Select
                    value={sourceCourseId}
                    onValueChange={(value) => { setSourceCourseId(value); setTemplateId(''); }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select a course" /></SelectTrigger>
                    <SelectContent>
                      {courses.length === 0 && <SelectItem value="none" disabled>No other courses assigned to you</SelectItem>}
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.course_code ? `${course.course_code} — ` : ''}{course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Or from a saved template</label>
                  <Select
                    value={templateId}
                    onValueChange={(value) => { setTemplateId(value); setSourceCourseId(''); }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select a template" /></SelectTrigger>
                    <SelectContent>
                      {templates.length === 0 && <SelectItem value="none" disabled>No templates saved yet</SelectItem>}
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-[#062B1A]/70">What to copy</p>
                <PartPicker include={include} onChange={setInclude} counts={sourceCounts} />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">If this course already has content</label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="append">Add alongside what is here</SelectItem>
                      <SelectItem value="replace">Replace the existing content</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Visibility of copied content</label>
                  <Select value={publishState} onValueChange={setPublishState}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Bring in as drafts to review</SelectItem>
                      <SelectItem value="keep">Keep the original published state</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {mode === 'replace' && (
                <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                  <p>
                    Replacing deletes this course's existing modules, lessons, assignments, exams and
                    materials for the parts you ticked, along with their uploaded files. Enrolments,
                    grades, attendance and student submissions are not affected.
                  </p>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button
                  className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                  disabled={working || (!sourceCourseId && !templateId)}
                  onClick={handleApply}
                >
                  {working ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Layers className="mr-2 size-4" />}
                  Copy design
                </Button>
              </DialogFooter>
            </TabsContent>

            <TabsContent value="save" className="space-y-4">
              <div className="grid gap-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Template name</label>
                  <Input
                    value={templateName}
                    onChange={(event) => setTemplateName(event.target.value)}
                    placeholder={courseTitle}
                    aria-label="Template name"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Notes (optional)</label>
                  <Textarea
                    value={templateDescription}
                    onChange={(event) => setTemplateDescription(event.target.value)}
                    placeholder="What this design is for, and anything to adjust when reusing it."
                    aria-label="Template notes"
                  />
                </div>
                <div>
                  <p className="mb-2 text-sm font-medium text-[#062B1A]/70">What to include</p>
                  <PartPicker include={saveInclude} onChange={setSaveInclude} counts={ownCounts} />
                </div>
              </div>

              {templates.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium text-[#062B1A]/70">Your saved templates</p>
                  <ul className="space-y-2">
                    {templates.map((template) => (
                      <li key={template.id} className="flex items-center gap-3 rounded-lg border border-[#062B1A]/10 bg-white p-3">
                        <Layers className="size-4 shrink-0 text-[#D4AF37]" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-[#062B1A]">{template.name}</p>
                          <p className="truncate text-xs text-[#062B1A]/45">
                            {template.source_course_title || 'Saved design'}
                            {template.counts ? ` · ${template.counts.lessons || 0} lessons, ${template.counts.assignments || 0} assignments` : ''}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          disabled={working}
                          onClick={() => handleDeleteTemplate(template)}
                          aria-label={`Delete ${template.name}`}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                <Button
                  className="bg-[#D4AF37] text-black hover:bg-[#B8941F]"
                  disabled={working}
                  onClick={handleSaveTemplate}
                >
                  {working ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Save className="mr-2 size-4" />}
                  Save as template
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
